import { ThemeProps } from 'styled-components';
import { DayPickerProps } from 'react-day-picker';

import {
  IThemeTwoInterface,
  VALID_BORDER_DATA,
  VALID_EDGE_STYLE_DATA,
  VALID_FONT_SIZE_DATA,
  VALID_OUTLINE_DATA,
  VALID_SHADOW_DATA,
} from '../../../components/_ThemeTwo_';
import { VALID_BREAKPOINTS } from '../StyledComponents';
import { VALID_ICONS } from '../../../components/Icon/SVGIcon';
import { VALID_GLOBAL_FONT_SIZES } from '../../../components/_ThemeTwo_/Typography/common';

export type VALID_FONTS =
  | 'opensans'
  | 'effra'
  | 'expert_sans'
  | 'expert_sans_bold'
  | 'expert_sans_sm'
  | 'expert_sans_sm_bold'
  | 'micr'
  | 'pnc_sans'
  | 'sailec'
  | 'universnext'
  | 'weblysleek'
  | 'stagbook'
  | 'proxima_nova'
  | 'benton_sans'
  | 'optimist'
  | 'optimist_light'
  | 'optimist_xlight'
  | 'optimist_semibold'
  | 'optimist_bold'
  | 'inter_regular'
  | 'inter_semibold'
  | 'inter_bold'
  | 'inter_light'
  | 'roboto'
  | 'lato';

export type VALID_RADIO_BORDER_WIDTH = '1px' | '2px' | '5px';
export type VALID_BUTTON_STYLE = 'primary' | 'secondary' | 'alert';
export type VALID_BUTTON_SIZE = 'tiny' | 'normal';
export type VALID_COLOR = 'alert' | 'success' | 'warning' | 'primary' | 'secondary' | 'default' | 'inactive';
export type VALID_HEADLINE_SCALE = 'small' | 'medium' | 'large';
export type VALID_SVG_COLOR = 'brand' | 'brandText' | 'link' | 'grey' | 'secondary' | 'tertiary' | 'error';
export type VALID_NOTIFICATION_LEVEL = 'error' | 'warning' | 'info' | 'success';

type ThemeValue = ((props: ThemeProps<IThemeInterface>) => string) | string;

// base level defaults
export const getOutline = ({ theme: { outline } }: ThemeProps<IThemeInterface>) => outline;

// basic color defaults
export const getColorBackground = ({ theme: { colorBackground } }: ThemeProps<IThemeInterface>) => colorBackground;
export const getColorWhite = ({ theme: { colorWhite } }: ThemeProps<IThemeInterface>) => colorWhite;
export const getColorBlack = ({ theme: { colorBlack } }: ThemeProps<IThemeInterface>) => colorBlack;

// brand color defaults
export const getColorPrimary = ({ theme: { brand: { colorPrimary } } }: ThemeProps<IThemeInterface>) => colorPrimary;
export const getColorPrimarySoft = ({ theme: { brand: { colorPrimarySoft } } }: ThemeProps<IThemeInterface>) => colorPrimarySoft;
export const getColorSecondary = ({ theme: { brand: { colorSecondary } } }: ThemeProps<IThemeInterface>) => colorSecondary;
export const getColorWarning = ({ theme: { brand: { colorWarning } } }: ThemeProps<IThemeInterface>) => colorWarning;
export const getColorAlert = ({ theme: { brand: { colorAlert } } }: ThemeProps<IThemeInterface>) => colorAlert;

// Slate color defaults
export const getColorSlate5 = ({ theme: { colorSlate5 } }: ThemeProps<IThemeInterface>) => colorSlate5;
export const getColorSlate10 = ({ theme: { colorSlate10 } }: ThemeProps<IThemeInterface>) => colorSlate10;
export const getColorSlate20 = ({ theme: { colorSlate20 } }: ThemeProps<IThemeInterface>) => colorSlate20;
export const getColorSlate50 = ({ theme: { colorSlate50 } }: ThemeProps<IThemeInterface>) => colorSlate50;
export const getColorSlate70 = ({ theme: { colorSlate70 } }: ThemeProps<IThemeInterface>) => colorSlate70;
export const getColorSlate = ({ theme: { colorSlate } }: ThemeProps<IThemeInterface>) => colorSlate;

interface IStandardProps {
  colorBackground: string;
  colorText: string;
  colorBorder: string;
}

interface ILoadingIconInsideButton {
  loadingIconInsideButton?: true;
  spinner?: {
    size: string;
    color: {
      circleOutline?: string;
      animatedOutline?: string;
    };
  };
}

interface IButtonProps extends ILoadingIconInsideButton {
  boxShadow: string;
  padding: string;
  colorBackground: string;
  colorBackgroundHover: string;
  colorBackgroundFocus: string;
  colorBackgroundDisabled: string;
  colorBorder: string;
  colorBorderHover: string;
  colorBorderDisabled: string;
  colorText: string;
  colorTextHover: string;
  colorTextDisabled: string;
  colorBorderFocus?: string;
  focusBoxShadow?: string;
}

interface IDefaultModal {
  width: string;
  padding: string;
  closeSize: string;
  closePadding: string;
  mobileTextAlign: string;
  headlineTextAlign: string;
  headlineScale: VALID_HEADLINE_SCALE;
  rowPadding: string;
}

interface IHeadlineProps {
  size: string;
  lineHeight: string;
  weight: string;
  sizeResponsive: string;
  lineHeightResponsive: string;
  displayTypeface: VALID_FONTS;
  colorText: string;
  letterSpacing: string;
}

export interface IThemeInterface {
  name: string;
  outline: string;
  globalAnchorDecoration: string;
  colorBackground: string;
  colorLineHeight: string;
  workflowLinkTextDecoration: string;

  colorWhite: string;
  colorSlate5: string;
  colorSlate10: string;
  colorSlate20: string;
  colorSlate50: string;
  colorSlate70: string;
  colorSlate: string;
  colorBlack: string;
  colorStatusWarning?: string;

  actionButton: {
    colorText: string;
    colorTextHover: string;
    colorTextActive: string;
    colorBorder: string;
    colorBorderHover: string;
    colorBackgroundActive: string;
  };
  alert: { [x in VALID_COLOR]: IStandardProps } & {
    borderWidth: string;
  };
  banner: { [x in VALID_COLOR]: IStandardProps & {
    colorLink: string;
    colorBackground: string;
    colorBorderLeft?: string;
  } } & {
    linkTextWeight: string;
    sectionBackground: string;
    paddingBanner: string;
    fontSizeBannerText: string;
    fontWeightBanner: string;
  };
  bannerSection: {
    colorBackground: string;
  };
  brand: {
    colorPrimary: string;
    colorPrimarySoft: string;
    colorSecondary: string;
    colorWarning: string;
    colorAlert: string;
  };
  button: { [x in VALID_BUTTON_STYLE]: IButtonProps } & {
    letterSpacing: string;
    focusBoxShadow?: string;
    fontWeight: string;
    fontSize: string;
    textTransform: string;
    borderWidth: string;
    borderRadius: string;
    tinySize: string; // font size
    tinyPadding: string;
    baseButtonMinWidth: string;
  };
  card: {
    borderRadius: string;
    borderWidth: string;
    colorBorder: string;
    boxShadow: string;
    colorBackground: string;
  };
  checkbox: {
    colorBorder: string;
    colorBackground: string;
    colorBackgroundChecked: string;
    colorBackgroundAlert: string;
    fontSize: string;
    error?: {
      border?: VALID_BORDER_DATA;
    };
  };
  datepicker: IDatePicker;
  header: {
    colorBreadcrumbText: string;
    colorHeaderDrawerLinkAboveSmallBP: string;
    colorLink: string;
    colorLinkHover: string;
    colorSubNavLink: string;
    colorSubNavLinkHover: string;
    subNavBreakPoint: VALID_BREAKPOINTS;
    summaryWeight: string;
    breadcrumbIcon?: {
      size?: string;
      color?: string;
      transform?: string;
      margin?: VALID_EDGE_STYLE_DATA;
    };
    collapsibleSubNav?: {
      height?: string;
    };
    headerSubNav?: {
      border?: VALID_BORDER_DATA;
    };
    headerSubNavInnerContainer?: {
      padding?: VALID_EDGE_STYLE_DATA;
    };
  };
  headline: { [x in VALID_HEADLINE_SCALE]: IHeadlineProps };
  icon: { [x in VALID_SVG_COLOR]: { colorIcon: string } };
  input: {
    alertBackgroundColor?: string;
    boxShadowHover: string;
    boxShadowAlert: string;
    colorBorder: string;
    colorBackground: string;
    colorPlaceholder: string;
    colorFocus: string;
    borderRadius: string;
    borderWidth: string;
    padding: string;
    colorTextAreaBorder: string;
    colorTextAreaBackground: string;
    selectInputBackgroundSvgFocused: string;
    textAreaPadding: string;
    colorText?: string;
    borderData?: VALID_BORDER_DATA;
    state?: {
      hover: {
        border?: VALID_BORDER_DATA;
      };
      focus: {
        border?: VALID_BORDER_DATA;
        outline?: VALID_OUTLINE_DATA;
      };
      error: {
        border?: VALID_BORDER_DATA;
        outline?: VALID_OUTLINE_DATA;
        colorText?: string;
      };
    };
  };
  label: {
    fontWeight: string;
    size: string;
    spacing: string;
    textTransform: string;
    datapointWeight: string;
    cardLabelSize: string;
    fontFamily?: VALID_FONTS;
    formLabelColor: string;
    formLabelMargin: string;
    formLabelWeight: string;
    smallTypeWeight: string;
    disabledColor?: string;
  };
  link: {
    colorLink: string;
    colorLinkIcon: string;
    colorLinkHover: string;
  };
  modal: IDefaultModal & {
    colorText: string;
    colorHeadlineText: string;
    colorBoxShadow: string;
    colorBackground: string; // this is the overlay background color.
    colorDrawerHover: string;
    colorDrawerBorder: string;
    colorDrawerBackgroundHover: string;
    colorClose: string;
    borderRadius: string;
    mobilePadding?: string;
    mobileColorBackground?: string;
    mobileCloseSize?: string;
  };
  notification: { [x in VALID_NOTIFICATION_LEVEL]: { colorBorder: string } } & {
    widthBorder: string;
    colorHover: string;
    spacing: string;
  };
  pill: { [x in VALID_COLOR]: {
    colorBackground: string;
    colorText: string;
  } } & {
    letterSpacing: string;
    padding: string;
    borderRadius: string;
    textTransform: string;
    fontSize: string;
  };
  progress: {
    colorBar: string;
    colorBarBackground: string;
    colorBarBorder: string;
    colorCircularBackground: string;
    circularSize: string;
    circularWeight: string;
  };
  progressTracker: {
    colorCompleted: string;
    colorOutstanding: string;
    colorProgressTrackerBackground: string;
    colorTextOutstanding: string;
    colorTextCompleted: string;
    colorStepBgCompleted: string;
    colorStepBorderCompleted: string;
    colorStepBgOutstanding: string;
    colorStepBorderOutstanding: string;
    colorHrOutstanding: string;
    colorHrCompleted: string;
    headerContainerPadding: string;
    hrHorizontalOffset: string;
    hrVerticalOffset: string;
    hrWidth: string;
    stepLabelFontWeight: string;
    stepLabelHorizontalOffset: string;
    stepNumberFontWeight: string;
    stepNumberFontSize: string;
    stepNumberVerticalOffset: string;
    svgCompletedHeight: string;
    svgCompletedWidth: string;
    trackerIndicatorWidth: string;
    trackerIndicatorHeight: string;
    trackerWrapperWidth: string;
    iconCheckedDone: VALID_ICONS;
  };
  radio: {
    colorBorder: string;
    colorBorderHover: string;
    colorBorderChecked: string;
    colorText: string;
    colorTextAlert: string;
    colorTextHover: string;
    colorTextChecked: string;
    colorBackground: string;
    colorBackgroundHover: string;
    colorBackgroundChecked: string;
    boxShadow: string;
    boxShadowHover: string;
    padding: string;
    borderWidth: VALID_RADIO_BORDER_WIDTH;
    fontWeight: string;
    borderRadius?: string;
    checked?: {
      labelColor?: string;
    };
    focus?: {
      outline?: VALID_OUTLINE_DATA;
      colorBackground?: string;
      border?: VALID_BORDER_DATA;
      colorText?: string;
    };
    disabled?: {
      border?: VALID_BORDER_DATA;
      labelColor?: string;
      colorBackground?: string;
    };
    disabledChecked?: {
      border?: VALID_BORDER_DATA;
      labelColor?: string;
      colorBackground?: string;
    };
    alertChecked?: {
      border?: VALID_BORDER_DATA;
      colorBackground?: string;
      colorText?: string;
    };
  };
  radioCard: {
    colorText: string;
    colorTextHover: string;
    colorBackgroundChecked: string;
    borderRadius?: string;
    boxShadow?: VALID_SHADOW_DATA;
    border?: VALID_BORDER_DATA;
    hover?: {
      colorBackground?: string;
      border?: VALID_BORDER_DATA;
    };
    focus?: {
      outline?: VALID_OUTLINE_DATA;
      border?: VALID_BORDER_DATA;
    };
    disabled?: {
      colorBackground?: string;
      border?: VALID_BORDER_DATA;
      boxShadow?: VALID_SHADOW_DATA;
    };
    checked?: {
      border?: VALID_BORDER_DATA;
    };
  };
  radioInput: {
    colorInner: string;
    colorBackground: string;
    colorBorder: string;
    colorBorderInactive: string;
    colorRadioLabelBackground: string;
    colorRadioLabelBackgroundActive: string;
    colorRadioButtonHover: string;
    borderWidth: string;
    borderWidthActive: string;
    hasActiveHeightAndWidth: boolean;
    labelTextOffset: string;
    positionRadioButtonInnerMarkTop: string;
    positionRadioButtonInnerMarkLeft: string;
    sizeRadioButton: string;
    sizeRadioButtonHover: string;
    sizeRadioButtonInnerMark: string;
    labelColorChecked?: string;
    labelColor?: string;
    hover?: {
      border?: VALID_BORDER_DATA;
    };
    disabled?: {
      colorBackground?: string;
      labelColor?: string;
      border?: VALID_BORDER_DATA;
    };
    disabledChecked?: {
      colorInner?: string;
    };
    error?: {
      labelColor?: string;
      border?: VALID_BORDER_DATA;
    };
  };
  spinner: {
    colorBackground: string;
    colorSegment: string;
  };
  table: { [x in VALID_COLOR]: { // all of these are pretty specific to servicing dash.
    colorText: string;
    colorIcon: string;
  } } & {
    colorDetailsButton: string;
    colorDivider: string;
    header: {
      fontWeight: string;
      colorBackground: string;
      colorSubHeaderBackground: string;
    };
    filter: {
      colorText: string;
      colorTextSelected: string;
      colorBackground: string;
      borderRadius: string;
    };
  };
  taskCard: {
    colorBorder: string;
    colorInReviewBorder: string;
    colorIcon: string;
    colorCtaIcon: string;
    colorText: string;
    colorCompleteText: string;
    colorSubhead: string;
    fontWeightHeadline: string;
    fontWeightSubHead: string;
    hasIcon: boolean;
    header?: {
      color?: {
        incomplete?: string;
      };
      lineHeight?: string;
      margin?: VALID_EDGE_STYLE_DATA;
      font?: {
        size?: VALID_GLOBAL_FONT_SIZES;
      };
    };
    statusPill?: {
      font?: {
        size?: VALID_GLOBAL_FONT_SIZES;
      };
    };
    ctaText?: {
      textDecoration?: string;
      color?: string;
    };
    card?: {
      padding: {
        mobile: string;
        small: string;
      };
      margin?: VALID_EDGE_STYLE_DATA;
    };
    taskThumb?: {
      margin?: {
        mobile: string;
        small: string;
      };
    };
  };
  text: {
    colorBullet?: string;
    colorText: string;
    colorCaption: string;
    formCaptionFontSize?: string;
    formCaptionFontFamily?: VALID_FONTS;
  };
  toolTip: {
    paneFontSize: string;
    paneFontFamily?: VALID_FONTS;
    paneBackgroundColor: string;
    paneBoxShadow: string;
    paneBorderRadius: string;
    fontColor?: string;
    border?: string;
  };
  typography: {
    bodyTypeface: VALID_FONTS;
    displayTypeface: VALID_FONTS;
  };
  upload: {
    colorBackground: string;
    colorBorder: string;
    colorText: string;
    colorActiveBackground: string;
    colorActiveBorder: string;
    borderStyle: string;
    borderWidth: string;
    size: string;
    width?: string;
  };
  verifications: {
    mastHeadlineColor: string;
  };

  themeTwo: IThemeTwoInterface;
}

export const defaultModal: IDefaultModal = {
  width: '680px',
  closeSize: '16px',
  closePadding: '1em',
  padding: '3.5em 6.5em',
  mobileTextAlign: 'center',
  headlineTextAlign: 'center',
  headlineScale: 'small',
  rowPadding: '0.75em',
};

interface IDatePicker {
  input: {
    icon: string;
    valueFormat?: string;
  };
  navButton: {
    color: ThemeValue;
    backgroundSize?: ThemeValue;
    // tslint:disable:prefer-method-signature
    nextIcon: (color: string) => string;
    prevIcon: (color: string) => string;
    // tslint:enable:prefer-method-signature
  };
  month?: {
    fontSize?: VALID_FONT_SIZE_DATA;
  };
  selection: {
    borderRadius: ThemeValue;
    backgroundColor: ThemeValue;
    color: ThemeValue;
  };
  weekday: {
    color: ThemeValue;
    fontSize?: VALID_FONT_SIZE_DATA;
    fontWeight?: string;
    border?: VALID_BORDER_DATA;
  };
  outline: ThemeValue;
  day: {
    hover: {
      color: ThemeValue;
      backgroundColor: ThemeValue;
    };
    disabled: {
      color: ThemeValue;
    };
    padding?: string;
    border?: VALID_BORDER_DATA;
    fontSize?: VALID_FONT_SIZE_DATA;
    leadingZero?: boolean;
  };
  overlay?: {
    borderRadius?: string;
  };
  dayPickerProps?: DayPickerProps;
}

export const defaultDatePicker: IDatePicker = {
  input: {
    // tslint:disable:max-line-length
    icon: "data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23A7B8C3' d='M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1c-.553 0-1 .447-1 1v12c0 .553.447 1 1 1h14c.553 0 1-.447 1-1V3c0-.553-.447-1-1-1zm-1 12H2V6h12v8z'/%3E%3C/svg%3E"
  },
  navButton: {
    color: getColorBlack,
    // tslint:disable:max-line-length
    nextIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='12'%3E%3Cpath fill='${encodeURIComponent(color)}' d='M9.53 5.1523l-8-4.9997A.998.998 0 0 0 .515.1256C.197.3026 0 .6366 0 1.0006V11c0 .364.197.698.515.875.152.083.318.125.485.125.184 0 .368-.051.53-.152l8-4.9998c.292-.183.47-.503.47-.848 0-.3449-.178-.6649-.47-.8479z'/%3E%3C/svg%3E%0A"`,
    prevIcon: (color: string) => `"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='12'%3E%3Cpath fill='${encodeURIComponent(color)}' %20transform%3D%27translate%2810%2C%200%29%20scale%28-1%2C%201%29%27 d='M9.53 5.1523l-8-4.9997A.998.998 0 0 0 .515.1256C.197.3026 0 .6366 0 1.0006V11c0 .364.197.698.515.875.152.083.318.125.485.125.184 0 .368-.051.53-.152l8-4.9998c.292-.183.47-.503.47-.848 0-.3449-.178-.6649-.47-.8479z'/%3E%3C/svg%3E%0A"`,
    // tslint:enable:max-line-length
    backgroundSize: '50%'
  },
  outline: getOutline,
  selection: {
    borderRadius: '2px',
    color: getColorPrimary,
    backgroundColor: getColorPrimarySoft,
  },
  weekday: {
    color: getColorSlate50
  },
  day: {
    hover: {
      color: getColorPrimary,
      backgroundColor: getColorPrimarySoft
    },
    disabled: {
      color: getColorSlate50
    },
  }

};

export const backgroundTint: number = 0.95;

export enum VALID_THEMES {
  avant = 'avant',
  eloan = 'eloan',
  regions = 'regions',
  td = 'td',
  hsbc = 'hsbc',
  pnc = 'pnc',
  synovus = 'synovus',
  amount = 'amount',
  amountDark = 'amountDark',
  bbva = 'bbva',
  cof = 'cof',
  barclays = 'barclays',
  catalyst = 'catalyst',
  'guaranteed-rate' = 'guaranteed-rate',
  jenius = 'jenius',
}

export const DEFAULT_THEME = VALID_THEMES.avant;
